import {Component, OnDestroy, OnInit, ViewChild, ChangeDetectionStrategy} from '@angular/core';
import {faPen} from '@fortawesome/free-solid-svg-icons';
import {LangChangeEvent, TranslateService} from '@ngx-translate/core';
import {ActivatedRoute, Router} from '@angular/router';
import {InformationState, SurveyDto, SurveyService} from '../../../_services/configuration-services';
import {DocumentViewComponent} from '../../../_shared/_components/document-management-component/document-view/document-view.component';
import {filter, Subscription, take} from 'rxjs';
import {TranslateUtils} from '../../../_shared/translate-utils';
import {DocumentManagementTypeEnum} from '../../../_shared/_components/document-management-component/document-management.component';
import {Location} from '@angular/common';
import {PermissionKeys} from '../../../app.constants';
import {CommunicationChannelConfigService} from '../../../_shared/communication-channel-config.service';
import {BackofficePermissionsService} from '../../../_shared/backoffice-permissions.service';
import {RolesService} from '../../../_shared/roles-service';

@Component({
    selector: 'app-view-survey',
    templateUrl: './view-survey.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ViewSurveyComponent implements OnInit, OnDestroy {
    PermissionKeys = PermissionKeys;
    InformationState = InformationState;
    DocumentManagementTypeEnum = DocumentManagementTypeEnum;

    @ViewChild('documentListComponent') documentListComponent: DocumentViewComponent;

    surveyId: string | null = null;
    survey!: SurveyDto;

    // A backoffice with the GestionCommunication permission previews the feed through the backoffice
    // endpoints; customers go through the my-surveys endpoints.
    canManage = false;

    // Icons
    icons = {
        pen: faPen
    };

    private langChangeSubscription: Subscription;
    crtLang = TranslateUtils.defaultLanguage;

    constructor(private readonly route: ActivatedRoute,
                private readonly router: Router,
                private readonly translateService: TranslateService,
                private readonly surveyService: SurveyService,
                private readonly communicationChannelConfigService: CommunicationChannelConfigService,
                private readonly backofficePermissionsService: BackofficePermissionsService,
                private readonly rolesService: RolesService,
                private readonly location: Location) {
    }

    ngOnInit(): void {
        this.crtLang = this.translateService.currentLang;
        this.langChangeSubscription = this.translateService.onLangChange.subscribe((event: LangChangeEvent) => {
            this.crtLang = event.lang;
        });

        this.route.params.pipe().subscribe(params => {
            this.surveyId = params['id'] ?? null;
            this.resolveCanManage(() => this.fetchSurvey(this.surveyId));
        });
    }

    ngOnDestroy(): void {
        this.langChangeSubscription.unsubscribe();
    }

    openSurvey(): void {
        // Read status is a customer-only concept; a backoffice preview must not mark it read.
        if (this.canManage) {
            window.open(this.survey.surveyUrl, '_blank');
            return;
        }
        this.surveyService.addReadStatus(this.survey.id).pipe().subscribe(_ => {
            this.communicationChannelConfigService.fetchHasUnreadMessage();
            window.open(this.survey.surveyUrl, '_blank');
        });
    }

    back(): void {
        this.location.back();
    }

    private fetchSurvey(surveyId: string): void {
        const request = this.canManage
            ? this.surveyService.getSurvey(surveyId)
            : this.surveyService.getMySurvey(surveyId);
        request.pipe().subscribe(survey => {
            this.survey = survey;

            if (this.documentListComponent) {
                this.documentListComponent.id = survey.id;
                this.documentListComponent.fetchData();
            }
        }, _ => {
            this.router.navigate(['..'], {
                relativeTo: this.route
            });
        });
    }

    // A backoffice with the GestionCommunication permission previews the feed through the backoffice
    // endpoints; everyone else (customers) goes through the my-surveys endpoints.
    private resolveCanManage(then: () => void): void {
        if (!this.rolesService.isBackofficeAccount()) {
            this.canManage = false;
            then();
            return;
        }
        this.backofficePermissionsService.permissionsLoaded$
            .pipe(filter(loaded => loaded), take(1))
            .subscribe(() => {
                this.canManage = this.backofficePermissionsService.hasPermission(PermissionKeys.gestionCommunication);
                then();
            });
    }
}
